import { type Comparable, groupBy } from '@luminovo/commons';
import { formatDecimal } from '@luminovo/frontend-utils';

import { type ChartDatum, type ChartSeriesDef, type ChartSeriesMeta, type ChartTransformerResult } from './types';

type GroupByConfig<TData> = {
    key: string;
    label: string | (() => string);
    /** Hover tooltip explaining this grouping mode. */
    tooltip?: string | (() => string);
    extractor: (row: TData) => string;
    /** Labels a group from its first row instead of the extractor's result. */
    labelExtractor?: (row: TData) => string;
};

export type ChartTransformerConfig<TData, TSharedContext> = {
    series: ChartSeriesDef<TData, TSharedContext>[];
    groupBy: GroupByConfig<TData>[];
    orderBy?: Comparable<ChartDatum<string>>;
    unstackedKey?: string;
    /** Set to false when the series' values don't add up, e.g. each is a maximum. Defaults to true. */
    stacked?: boolean;
    /** The chart-level value formatter, e.g. for currency; wins over the series'. */
    formatValue?: (ctx: TSharedContext) => (value: number) => string;
};

export type ChartTransformer<TData, TSharedContext> = {
    transform(data: TData[], groupByKey: string, context: TSharedContext): ChartTransformerResult<string>;
    groupByOptions: Array<{ key: string; label: string | (() => string); tooltip?: string | (() => string) }>;
};

export function resolveLabel(label: string | (() => string)): string {
    return typeof label === 'function' ? label() : label;
}

export function createChartTransformer<TData, TSharedContext = void>(
    config: ChartTransformerConfig<TData, TSharedContext>,
): ChartTransformer<TData, TSharedContext> {
    const { series, orderBy, unstackedKey } = config;
    const keys = series.map((s) => s.key);

    const groupByOptions = config.groupBy.map((g) => ({
        key: g.key,
        label: g.label,
        tooltip: g.tooltip,
    }));

    return {
        groupByOptions,

        transform(data: TData[], groupByKey: string, context: TSharedContext): ChartTransformerResult<string> {
            const groupByConfig = config.groupBy.find((g) => g.key === groupByKey) ?? config.groupBy[0];

            const grouped = groupBy(data, (row) => groupByConfig.extractor(row));

            const chartData: ChartDatum<string>[] = Object.entries(grouped).map(([groupKey, rows]) => {
                const displayLabel =
                    groupByConfig.labelExtractor && (rows as TData[]).length > 0
                        ? groupByConfig.labelExtractor((rows as TData[])[0])
                        : groupKey;

                // Unsound in theory: series keys never collide with `label` or `sourceRows`, so the cast holds.
                const datum = {
                    label: displayLabel,
                    sourceRows: rows as unknown[],
                } as ChartDatum<string>;

                for (const s of series) {
                    const values = (rows as TData[]).map((row) => s.extractValue(row, context));
                    datum[s.key] = s.aggregateFn(values);
                }

                return datum;
            });

            if (orderBy) {
                chartData.sort(orderBy);
            }

            // Config-level factory, then the first series' resolveFormatValue, then formatDecimal.
            let chartFormatValue: ((value: number) => string) | undefined = config.formatValue?.(context);

            const seriesMeta = {} as Record<string, ChartSeriesMeta>;

            for (const s of series) {
                const resolvedFormatValue = s.resolveFormatValue?.(context, data);
                if (resolvedFormatValue && !chartFormatValue) {
                    chartFormatValue = resolvedFormatValue;
                }

                seriesMeta[s.key] = {
                    label: resolveLabel(s.label),
                    color: s.color,
                    tooltip: s.tooltip ? resolveLabel(s.tooltip) : undefined,
                    status: s.status,
                    // A status counts line items, so it never takes the chart's unit, e.g. its currency.
                    formatValue: s.status
                        ? (s.formatValue ?? ((value: number) => formatDecimal(value)))
                        : (resolvedFormatValue ?? s.formatValue),
                };
            }

            return {
                data: chartData,
                keys,
                seriesMeta: seriesMeta as Record<string, ChartSeriesMeta>,
                formatValue: chartFormatValue ?? ((value: number) => formatDecimal(value)),
                unstackedKey,
                stacked: config.stacked ?? true,
            };
        },
    };
}
